// Selector de tags de entitlements del plan (`plan_tags`, migration 0034).
//
// Vive como seccion propia de PlanDetail y NO dentro del diálogo de publicar
// version, a proposito: los tags son la unica parte de la config del plan que
// NO es versionada (ver cabecera de 0034). Meterlos en el diálogo daria a
// entender que cambiarlos publica una version y exige motivo — no es asi.
//
// El selector solo ofrece tags `active` del catalogo. Un tag archivado que ya
// estaba asignado se sigue mostrando (y sigue resolviendo del lado de la OTT):
// archivar saca de circulacion para asignaciones NUEVAS, no rompe accesos.
//
// El boton "Gestionar tags" abre TagManagerModal sin salir de la pantalla —
// para poder definir un tag en el momento en que hace falta asignarlo.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Settings2, X } from 'lucide-react';
import TagManagerModal, { TagChip } from './TagManagerModal';
import {
  listPartnerTags,
  listPlanTags,
  setPlanTags,
  AdminApiError,
  type PlanTagAssignment,
} from '@/lib/api';
import { fieldCls } from '@/lib/form-styles';

/** `datetime-local` no lleva zona; el backend pide ISO con offset. */
const toLocalInput = (iso: string | null): string => (iso ? iso.slice(0, 16) : '');
const fromLocalInput = (local: string): string | null =>
  local.trim() === '' ? null : new Date(local).toISOString();

export default function PlanTagsEditor({
  planId,
  partnerId,
  canWrite,
}: {
  planId: string;
  partnerId: string;
  canWrite: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [managing, setManaging] = useState(false);
  // null = todavia no se toco nada; se trabaja sobre lo que vino del server.
  const [draft, setDraft] = useState<PlanTagAssignment[] | null>(null);
  const [adding, setAdding] = useState('');

  const planTagsQ = useQuery({
    queryKey: ['admin-plan-tags', planId],
    queryFn: () => listPlanTags(planId),
  });
  const catalogQ = useQuery({
    queryKey: ['admin-partner-tags', partnerId],
    queryFn: () => listPartnerTags(partnerId),
  });

  const serverTags: PlanTagAssignment[] = useMemo(
    () =>
      (planTagsQ.data?.tags ?? []).map((t) => ({
        tag: t.tag,
        valid_from: t.valid_from,
        valid_until: t.valid_until,
      })),
    [planTagsQ.data],
  );
  const current = draft ?? serverTags;

  const meta = useMemo(() => {
    const map = new Map(catalogQ.data?.map((t) => [t.tag, t]) ?? []);
    return map;
  }, [catalogQ.data]);

  // Ofrecer solo los `active` que no estén ya puestos. Un archivado asignado
  // sigue visible arriba, pero no vuelve a ofrecerse.
  const available = useMemo(
    () =>
      (catalogQ.data ?? []).filter(
        (t) => t.status === 'active' && !current.some((c) => c.tag === t.tag),
      ),
    [catalogQ.data, current],
  );

  const dirty = useMemo(
    () => JSON.stringify(current) !== JSON.stringify(serverTags),
    [current, serverTags],
  );

  const save = useMutation({
    mutationFn: () => setPlanTags(planId, current),
    onSuccess: async () => {
      setDraft(null);
      await qc.invalidateQueries({ queryKey: ['admin-plan-tags', planId] });
      await qc.invalidateQueries({ queryKey: ['admin-partner-tags', partnerId] });
    },
  });

  const update = (i: number, patch: Partial<PlanTagAssignment>): void =>
    setDraft(current.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  const removeAt = (i: number): void => setDraft(current.filter((_, idx) => idx !== i));

  return (
    <section className="rounded-lg border border-surface-border p-4 dark:border-surface-dark-border">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-ink dark:text-ink-inverted">
            Tags de entitlements
          </h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            Viajan a la OTT en{' '}
            <span className="font-mono">/users/&#123;userId&#125;/entitlements</span> y se pueden
            exigir en <span className="font-mono">/entitlements/check</span>. No se versionan:
            se guardan al toque, sin publicar una versión.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setManaging(true)}
          className="inline-flex min-h-[2.25rem] shrink-0 items-center gap-1.5 rounded-lg border border-surface-border px-3 text-sm text-ink hover:bg-ink-muted/5 dark:border-surface-dark-border dark:text-ink-inverted"
        >
          <Settings2 aria-hidden className="h-4 w-4" />
          Gestionar tags
        </button>
      </div>

      {planTagsQ.isLoading || catalogQ.isLoading ? (
        <p className="mt-3 text-sm text-ink-muted">Cargando…</p>
      ) : null}

      {catalogQ.data?.length === 0 ? (
        <p className="mt-3 text-sm text-ink-muted">
          Este partner todavía no tiene tags en el catálogo.{' '}
          <button
            type="button"
            onClick={() => setManaging(true)}
            className="text-brand hover:underline"
          >
            Crear el primero
          </button>
          .
        </p>
      ) : null}

      {current.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {current.map((a, i) => {
            const m = meta.get(a.tag);
            return (
              <li
                key={a.tag}
                className="rounded-lg border border-surface-border p-2.5 dark:border-surface-dark-border"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <TagChip
                    tag={a.tag}
                    color={m?.color ?? null}
                    muted={m?.status === 'archived'}
                  />
                  <span className="flex-1 text-sm text-ink dark:text-ink-inverted">
                    {m?.display_name ?? a.tag}
                  </span>
                  {m?.status === 'archived' ? (
                    <span className="rounded bg-ink-muted/20 px-1.5 py-0.5 text-xs text-ink-muted">
                      archivado en el catálogo
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    disabled={!canWrite}
                    aria-label={`Quitar ${a.tag}`}
                    className="rounded p-1 text-ink-muted hover:bg-ink-muted/10 disabled:opacity-50"
                  >
                    <X aria-hidden className="h-4 w-4" />
                  </button>
                </div>

                {/* Ventana de vigencia: es lo que hace util a los tags para
                    promos (ej: 'live_sports' solo durante el torneo). Vacío en
                    ambos = siempre vigente. */}
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs text-ink-muted">Desde (opcional)</span>
                    <input
                      type="datetime-local"
                      value={toLocalInput(a.valid_from)}
                      onChange={(e) => update(i, { valid_from: fromLocalInput(e.target.value) })}
                      disabled={!canWrite}
                      className={fieldCls({ size: 'sm' })}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-ink-muted">Hasta (opcional)</span>
                    <input
                      type="datetime-local"
                      value={toLocalInput(a.valid_until)}
                      onChange={(e) => update(i, { valid_until: fromLocalInput(e.target.value) })}
                      disabled={!canWrite}
                      className={fieldCls({ size: 'sm' })}
                    />
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {available.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            disabled={!canWrite}
            aria-label="Tag a agregar"
            className={fieldCls({ block: false })}
          >
            <option value="">Elegí un tag…</option>
            {available.map((t) => (
              <option key={t.tag} value={t.tag}>
                {t.display_name} ({t.tag})
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              if (!adding) return;
              setDraft([...current, { tag: adding, valid_from: null, valid_until: null }]);
              setAdding('');
            }}
            disabled={!canWrite || adding === ''}
            className="inline-flex min-h-[2.5rem] items-center gap-1.5 rounded border border-surface-border px-3 text-sm disabled:opacity-50 dark:border-surface-dark-border"
          >
            <Plus aria-hidden className="h-4 w-4" />
            Agregar
          </button>
        </div>
      ) : null}

      {save.isError ? (
        <p className="mt-3 text-sm text-status-error">
          {save.error instanceof AdminApiError
            ? save.error.message
            : 'No se pudieron guardar los tags.'}
        </p>
      ) : null}

      {dirty ? (
        <div className="mt-3 flex items-center justify-end gap-2">
          <p className="mr-auto text-xs text-ink-muted">
            Los cambios tardan hasta el TTL de caché del partner en verse desde la OTT.
          </p>
          <button
            type="button"
            onClick={() => setDraft(null)}
            className="min-h-[2.5rem] rounded-lg border border-surface-border px-3 text-sm dark:border-surface-dark-border"
          >
            Descartar
          </button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!canWrite || save.isPending}
            className="min-h-[2.5rem] rounded-lg bg-brand px-4 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50"
          >
            {save.isPending ? 'Guardando…' : 'Guardar tags'}
          </button>
        </div>
      ) : null}

      <TagManagerModal
        open={managing}
        partnerId={partnerId}
        canWrite={canWrite}
        onClose={() => setManaging(false)}
      />
    </section>
  );
}
